import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';

/**
 * Specialiserad komponent för att skicka kontaktförfrågningar via Leadme-teamet
 * (inredningsarkitekt eller byggkonsult)
 */
const LeadmeKontakt = ({
  isOpen,
  onClose,
  supplier,
  responsible,
  projects = [],
  activeProject = null,
  procurement = null,
  onRequestSaved
}) => {
  const { user } = useAuth();
  // Förfrågan ska knytas till projektet kunden tittar på, inte till det första
  // i listan — kunder kan ha flera projekt med olika arkitekter.
  const projekt = activeProject || projects?.[0] || null;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactResult, setContactResult] = useState({ success: false, message: '' });
  
  // Stäng modalen om isOpen ändras till false
  useEffect(() => {
    if (!isOpen) {
      // Återställ tillstånd om modalen stängs
      setContactResult({ success: false, message: '' });
      setIsSubmitting(false);
    }
  }, [isOpen]);


  // Hantera skickande av kontaktförfrågan
  const handleSubmit = async () => {
    if (!responsible || !supplier) {
      setContactResult({
        success: false,
        message: 'Det uppstod ett fel. Vänligen försök igen senare.'
      });
      return;
    }

    setIsSubmitting(true);
    
    
    // Hämta kontaktinformation från rätt objekt
    const getContactInfo = () => {
      // Försök hämta från primaryContactPerson först (det nya systemet)
      if (supplier.primaryContactPerson) {
        return {
          contactPerson: `${supplier.primaryContactPerson.firstName || ''} ${supplier.primaryContactPerson.lastName || ''}`.trim() || 'Ej angivet',
          email: supplier.primaryContactPerson.email || supplier.email || 'Ej angivet',
          phone: supplier.primaryContactPerson.phone || supplier.phone || 'Ej angivet'
        };
      }
      
      // Ingen användarkopplad kontakt. Saknas även namnet men e-post finns,
      // visa e-posten som kontakt i stället för "Ej angivet" — den räcker för
      // att ta kontakt, och det är ofta allt vi har för mindre leverantörer.
      return {
        contactPerson: supplier.contactPerson || supplier.email || 'Ej angivet',
        email: supplier.email || 'Ej angivet',
        phone: supplier.phone || 'Ej angivet'
      };
    };

    const contactInfo = getContactInfo();
    
    try {
      const token = localStorage.getItem('token');
      
      // Skapa ett detaljerat e-postmeddelande
      const messageBody = `
Hej ${responsible.firstName}!

Jag är intresserad av att komma i kontakt med leverantören ${supplier.companyName} som erbjuder ${supplier.discountRate}% rabatt.

Leverantörsinformation:
- Företag: ${supplier.companyName}
- Kontaktperson: ${contactInfo.contactPerson}
- E-post: ${contactInfo.email}
- Telefon: ${contactInfo.phone}
- Typ: ${supplier.supplierType}

Vänligen hjälp mig att komma i kontakt med dem.

Med vänlig hälsning,
${user ? `${user.firstName} ${user.lastName}` : 'En kund'}
${user?.email ? `E-post: ${user.email}` : ''}
${user?.phone ? `Tel: ${user.phone}` : ''}
`;

      // Använd mail API för att skicka e-post
      const response = await fetch(`${API_URL}/api/mail/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: responsible.email,
          subject: `${user ? `${user.firstName} ${user.lastName}` : 'Kund'}${projekt?.projectId ? ` (#${projekt.projectId})` : ''} vill komma i kontakt med ${supplier.companyName}`,
          message: messageBody,
          senderName: user ? `${user.firstName} ${user.lastName}` : 'Leadme Kundportal',
          recipientName: `${responsible.firstName} ${responsible.lastName}`
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Kunde inte skicka meddelande');
      }

      // Spara kontaktförfrågan i databasen
      try {
        await fetch(`${API_URL}/api/supplier-contact-requests`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            supplierId: supplier._id,
            procurementId: procurement?._id,
            projectId: projekt?._id
          })
        });
        if (onRequestSaved) onRequestSaved();
      } catch {}

      setContactResult({
        success: true,
        message: `Din förfrågan har skickats till teamet på Leadme. Vi kommer att hjälpa dig att komma i kontakt med ${supplier.companyName}.`
      });
    } catch (error) {
      console.error('Fel vid kontaktförfrågan:', error);
      setContactResult({
        success: false,
        message: 'Ett fel uppstod när förfrågan skulle skickas. Vänligen försök igen senare.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-lg overflow-hidden max-w-md w-full">
        <div className="p-6">
          <h3 className="text-xl font-semibold mb-2">
            {contactResult.success ? 'Förfrågan skickad' : (responsible ? 'Kontaktförfrågan' : 'Information')}
          </h3>
          
          {contactResult.success ? (
            // Visa lyckad förfrågan
            <div>
              <div className="mb-6 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                  <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-gray-600">{contactResult.message}</p>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-leadme-pink text-white rounded-lg hover:bg-leadme-pink-dark"
                >
                  Stäng
                </button>
              </div>
            </div>
          ) : contactResult.message ? (
            // Visa felmeddelande
            <div>
              <div className="mb-6 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                  <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <p className="text-gray-600">{contactResult.message}</p>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-leadme-pink text-white rounded-lg hover:bg-leadme-pink-dark"
                >
                  Stäng
                </button>
              </div>
            </div>
          ) : (
            // Visa bekräftelsedialog
            <div>
              <p className="text-gray-600 mb-6">
                Du är på väg att skicka en förfrågan om att komma i kontakt med {supplier.companyName}.
                Din förfrågan kommer skickas till teamet på Leadme som hjälper dig vidare.
              </p>
              
              <div className="flex justify-end space-x-4">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Avbryt
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-leadme-pink text-white rounded-lg hover:bg-leadme-pink-dark disabled:bg-leadme-pink-light"
                >
                  {isSubmitting ? 
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Skickar...
                    </span> : 
                    'Skicka förfrågan'
                  }
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadmeKontakt; 